import React from "react";
import { Tooltip, Icon } from "antd";
import Moment from "react-moment";
import styled from "styled-components";

import { UsersAssigned } from "~/components/AssignTo";
import StagesStrip from "~/_domains/societario/components/StagesStrip";
import {
  getBackgroundItem,
  getTextItem,
  getCategoryLabel,
  getChargeTone,
  withAlpha,
} from "~/_domains/societario/helpers";

// Cartao do painel do societario.
//
// A versao anterior usava a cor de fundo do cartao inteiro como unico sinal do
// estado da cobranca: metade do quadro ficava verde-agua, o rotulo de categoria
// vinha numa tarja saturada e a palavra "Cobranca" flutuava solta ao lado. Com
// tudo disputando a mesma atencao, nada se lia primeiro.
//
// Agora a hierarquia e explicita: identificacao no topo, nome do cliente como
// primeira linha de leitura, e os sinais (cobranca, NF, responsavel, etapas) num
// rodape de apoio. O estado da cobranca migrou para a faixa lateral esquerda
// somada a um selo com texto — cor deixou de ser a unica portadora do recado.
const Card = styled.div`
  user-select: none;
  position: relative;
  margin: 0 0 10px 0;
  padding: 10px 12px 12px 12px;
  border: 1px solid #e8e8e8;
  border-left: 3px solid ${(props) => props.accent};
  border-radius: 6px;
  background: ${(props) => props.tint};
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #d9d9d9;
    border-left-color: ${(props) => props.accent};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.09);
    transform: translateY(-1px);
  }

  ${(props) => props.dragging && `
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
    transform: none;
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// A tarja saturada virou etiqueta: mesma cor da categoria, agora como texto
// sobre um fundo translucido. Encolhe com elipse quando o rotulo e longo
// ("Troca de Contabilidade") em vez de empurrar o numero do ticket para fora.
const Category = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  color: ${(props) => props.tone};
  background: ${(props) => withAlpha(props.tone, 0.1)};
  border: 1px solid ${(props) => withAlpha(props.tone, 0.28)};
`;

const TicketId = styled.span`
  flex: none;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #bfbfbf;
`;

// Cortar o nome em 25 caracteres desperdicava a largura disponivel e cortava
// tambem quando havia sobra. A elipse do CSS usa o espaco que existe.
const Title = styled.div`
  display: flex;
  align-items: center;
  margin: 7px 0 9px 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #262626;
`;

const TitleText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CustomerCode = styled.span`
  color: #8c8c8c;
  font-weight: 500;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  min-height: 22px;
`;

const MetaRight = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-left: 8px;
`;

const ChargeBadge = styled.span`
  flex: none;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  color: ${(props) => props.tone.color};
  background: ${(props) => props.tone.background};
  border: 1px solid ${(props) => props.tone.border};
`;

const When = styled.span`
  margin-right: 8px;
  font-size: 11px;
  color: #8c8c8c;
  white-space: nowrap;
`;

// O indicador de checklist ficava em `bottom: -5px`, para fora do cartao, e
// esbarrava no cartao de baixo. Agora e um fio rente a borda inferior.
const ChecklistTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  overflow: hidden;
  border-radius: 0 0 5px 5px;
  background: #f0f0f0;
`;

const ChecklistFill = styled.div`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: #52c41a;
  transition: width 0.3s ease;
`;

// `allow_nf` e coluna da tabela de clientes. Para as categorias 1 e 5 ela chega
// dentro de custom_fields (merge feito pelo accessor do model), mas a listagem
// do painel e query bruta e nao passa por ele — por isso o dado tambem e lido
// do cliente, que e de onde ele realmente vem.
const allowsNf = (item, customer) => {
  if (item.custom_fields && item.custom_fields.allow_nf) {
    return true;
  }
  return !!(customer && customer.allow_nf);
};

export const ItemCard = ({
  item,
  provided,
  snapshot,
  getCustomer,
  showDrawer,
  hasContract,
}) => {
  const customer = getCustomer(item);
  const tasks = Array.isArray(item.tasks) ? item.tasks : [];
  const done = tasks.filter((task) => task.done).length;
  const progress = tasks.length > 0 ? (done / tasks.length) * 100 : 0;
  const assignedTo = (item.custom_fields && item.custom_fields.assigned_to) || [];
  const charge = getChargeTone(item);
  const categoryColor = getBackgroundItem(item.category);

  return (
    <Card
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      dragging={snapshot.isDragging ? 1 : 0}
      accent={charge.accent}
      tint={charge.tint}
      onClick={() =>
        showDrawer(parseInt(item.category), {
          ...item,
          customer: customer,
        })
      }
    >
      <Header>
        <Tooltip title={getCategoryLabel(item.category)}>
          <Category tone={categoryColor}>{getTextItem(item.category)}</Category>
        </Tooltip>
        <TicketId>#SOC{item.id}</TicketId>
      </Header>

      <Title>
        {hasContract(item) && (
          <Tooltip title="Contrato ou distrato registrado">
            <Icon
              type="file-protect"
              style={{ color: '#52c41a', marginRight: 6, fontSize: 14, flex: 'none' }}
            />
          </Tooltip>
        )}
        <Tooltip title={customer.name}>
          <TitleText>
            {customer.cod && <CustomerCode>{customer.cod} · </CustomerCode>}
            {customer.name}
          </TitleText>
        </Tooltip>
      </Title>

      <Meta>
        <Tooltip title={charge.title}>
          <ChargeBadge tone={charge}>{charge.label}</ChargeBadge>
        </Tooltip>
        {allowsNf(item, customer) && (
          <Tooltip title="Serviço de emissão de NF contratado">
            <Icon
              type="file-text"
              style={{ color: '#1890ff', marginLeft: 8, fontSize: 14 }}
            />
          </Tooltip>
        )}
        <MetaRight>
          <Tooltip title="Última movimentação">
            <When>
              <Moment fromNow>{item.updated_at}</Moment>
            </When>
          </Tooltip>
          <UsersAssigned selectedUsers={assignedTo} />
        </MetaRight>
      </Meta>

      <StagesStrip item={item} />

      {progress > 0 && (
        <Tooltip title={done + ' de ' + tasks.length + ' itens do checklist concluídos'}>
          <ChecklistTrack>
            <ChecklistFill percent={progress} />
          </ChecklistTrack>
        </Tooltip>
      )}
    </Card>
  );
};

export default ItemCard;
